import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, AlertTriangle } from 'lucide-react';
import {
  fetchEstimateWorkspace,
  fetchEstimateForEdit,
  loadEstimateDraft,
  saveEstimateDraft,
  createEstimate,
  updateEstimate,
  checkCustomerDuplicate,
  searchEstimateCustomers,
  acquireEditLock,
  releaseEditLock,
  heartbeatEditLock,
  type EstimateFormState,
  type EstimateLineItem,
  type EstimateCustomerMatch,
  type PriceBookItem,
} from '../lib/bridge';
import { LoadingState } from '../components/ui/LoadingState';
import { ErrorState } from '../components/ui/ErrorState';
import { EmptyState } from '../components/ui/EmptyState';
import { Button } from '../components/ui/Button';
import { Card, CardHeader } from '../components/ui/Card';
import { fieldControlClass, fieldControlSmClass, fieldLabelClass } from '../components/ui/forms';

const LEAD_SOURCES = ['Referral', 'Website', 'Google', 'Repeat Customer', 'Other'];

function emptyForm(): EstimateFormState {
  return {
    customerName: '',
    phone: '',
    email: '',
    billingAddress: '',
    serviceAddress: '',
    project: '',
    leadSource: 'Other',
    customerNotes: '',
    paymentTerms: 'Due upon receipt',
    discount: 0,
    taxRate: 0,
    items: [],
  };
}

function money(n: number) {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

// Full/Quick Estimate builder (docs/V6_ARCHITECTURE_AND_MIGRATION_PLAN.md
// §8, Stage 3) — the one write workflow this app deliberately held back
// until every simpler stage was built and verified, given how financially
// central it is. Every save (draft, create, update) goes straight through
// the existing, unmodified appSaveEstimateDraft/appCreateEstimate/
// appUpdateEstimate — see the comment on `estimateWorkspace` in
// src/V6Api.js for exactly what stays enforced (and what deliberately
// isn't, e.g. no margin-floor check at this stage — that lives in the
// Change Order path).
export function EstimateBuilderPage() {
  const [params] = useSearchParams();
  const documentNo = params.get('documentNo') || '';
  const draftIdParam = params.get('draftId') || '';
  const editing = !!documentNo;

  const workspaceQuery = useQuery({ queryKey: ['estimateWorkspace'], queryFn: fetchEstimateWorkspace });
  const editQuery = useQuery({ queryKey: ['estimateForEdit', documentNo], queryFn: () => fetchEstimateForEdit(documentNo), enabled: editing });
  const draftQuery = useQuery({ queryKey: ['estimateDraft', draftIdParam], queryFn: () => loadEstimateDraft(draftIdParam), enabled: !!draftIdParam });

  const [form, setForm] = useState<EstimateFormState>(emptyForm());
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerMatches, setCustomerMatches] = useState<EstimateCustomerMatch[]>([]);
  const [priceBookPick, setPriceBookPick] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ kind: 'success' | 'error'; text: string; jobId?: string; pdfUrl?: string } | null>(null);

  // Concurrent-edit lock — matches the existing HTML UI's per-entity lock
  // (10-minute expiry server-side), so a second employee opening the same
  // estimate is warned instead of silently overwriting the first editor's
  // changes. Acquired once editing an existing estimate is confirmed
  // loaded, heartbeated every 4 minutes, released on unmount.
  const lockSessionId = useRef(typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : String(Math.random())).current;
  const [lockState, setLockState] = useState<{ locked: boolean; holderName?: string } | null>(null);

  useEffect(() => {
    if (!editing || !loaded || !documentNo) return;
    let cancelled = false;
    // Best-effort only: this lock is a UX courtesy warning a second editor
    // away, not the actual data-integrity mechanism (appSaveEstimateDraft/
    // appUpdateEstimate already serialize writes server-side regardless).
    // A failed acquire/heartbeat/release (network hiccup, page unloading)
    // must never surface as an unhandled rejection or block editing, so
    // every call here is swallowed on failure and simply leaves the lock
    // state as "unlocked" rather than erroring.
    acquireEditLock('ESTIMATE', documentNo, lockSessionId)
      .then((result) => {
        if (!cancelled) setLockState(result.locked ? { locked: true, holderName: result.holderName } : { locked: false });
      })
      .catch(() => {});
    const heartbeat = setInterval(() => {
      heartbeatEditLock('ESTIMATE', documentNo, lockSessionId)
        .then((result) => {
          if (!cancelled) setLockState(result.locked ? { locked: true, holderName: result.holderName } : { locked: false });
        })
        .catch(() => {});
    }, 4 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(heartbeat);
      releaseEditLock('ESTIMATE', documentNo, lockSessionId).catch(() => {});
    };
  }, [editing, loaded, documentNo, lockSessionId]);

  useEffect(() => {
    if (loaded) return;
    if (editing && editQuery.data?.ok && editQuery.data.estimate) {
      // Merge over emptyForm() rather than the raw response so every
      // controlled input always has a defined value, even if a field is
      // omitted from the response (avoids React's controlled/uncontrolled
      // input warning and the input silently losing its handlers).
      setForm({ ...emptyForm(), ...editQuery.data.estimate, documentNo });
      setLoaded(true);
    } else if (draftIdParam && draftQuery.data?.ok && draftQuery.data.draft) {
      setForm({ ...emptyForm(), ...draftQuery.data.draft, draftId: draftIdParam });
      setLoaded(true);
    } else if (!editing && !draftIdParam) {
      setLoaded(true);
    }
  }, [editing, editQuery.data, draftIdParam, draftQuery.data, documentNo, loaded]);

  const totals = useMemo(() => {
    const subtotal = form.items.reduce((n, x) => n + Number(x.qty || 0) * Number(x.rate || 0), 0);
    const discount = Number(form.discount || 0);
    const discounted = Math.max(0, subtotal - discount);
    const taxRate = Number(form.taxRate || 0);
    const tax = discounted * (taxRate > 1 ? taxRate / 100 : taxRate);
    return { subtotal, discounted, tax, total: discounted + tax };
  }, [form.items, form.discount, form.taxRate]);

  const duplicateCheck = useMutation({ mutationFn: () => checkCustomerDuplicate(form) });
  const customerSearchMutation = useMutation({
    mutationFn: (q: string) => searchEstimateCustomers(q),
    onSuccess: (result) => setCustomerMatches(result.customers ?? []),
  });

  const saveDraftMutation = useMutation({
    mutationFn: () => saveEstimateDraft(form),
    onSuccess: (result) => {
      if (result.ok) {
        setForm((prev) => ({ ...prev, draftId: result.draftId }));
        setSaveMessage({ kind: 'success', text: `Draft saved (${result.totalText}).` });
      } else {
        setSaveMessage({ kind: 'error', text: result.message || 'Could not save the draft.' });
      }
    },
  });

  const submitMutation = useMutation({
    mutationFn: () => (editing ? updateEstimate(form) : createEstimate(form)),
    onSuccess: (result) => {
      if (result.ok) {
        setSaveMessage({ kind: 'success', text: editing ? 'Estimate updated.' : `Estimate ${result.documentNo} created.`, jobId: result.jobId, pdfUrl: result.pdfUrl });
      } else {
        setSaveMessage({ kind: 'error', text: result.message || 'Could not save this estimate.' });
      }
    },
  });

  function updateItem(index: number, patch: Partial<EstimateLineItem>) {
    setForm((prev) => ({ ...prev, items: prev.items.map((it, i) => (i === index ? { ...it, ...patch } : it)) }));
  }

  function removeItem(index: number) {
    setForm((prev) => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
  }

  function addCustomItem() {
    setForm((prev) => ({
      ...prev,
      items: [...prev.items, { category: 'Custom', description: '', qty: 1, unit: 'item', rate: 0, priceSource: 'custom' }],
    }));
  }

  function addPriceBookItem() {
    const item = (workspaceQuery.data?.priceBook ?? []).find((p) => p.serviceId === priceBookPick);
    if (!item) return;
    setForm((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        { serviceId: item.serviceId, category: item.category, description: item.name, qty: item.defaultQty as number ?? 1, unit: item.unit, rate: item.currentPrice, priceSource: 'price-book' },
      ],
    }));
    setPriceBookPick('');
  }

  if (workspaceQuery.isLoading || (editing && editQuery.isLoading) || (!!draftIdParam && draftQuery.isLoading)) {
    return <LoadingState label="Loading estimate…" />;
  }
  if (workspaceQuery.isError || !workspaceQuery.data?.ok) {
    return <ErrorState message={workspaceQuery.data?.message || 'Could not load the estimate workspace.'} onRetry={() => workspaceQuery.refetch()} />;
  }
  if (editing && editQuery.data && !editQuery.data.ok) {
    return <ErrorState message={editQuery.data.message || 'Could not load this estimate for editing.'} />;
  }

  const priceBook: PriceBookItem[] = workspaceQuery.data.priceBook ?? [];

  return (
    <div className="space-y-5 pb-10">
      <div>
        <Link to="/estimates" className="inline-flex items-center gap-1.5 text-sm font-semibold text-gold-300 hover:text-gold-200">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Estimates
        </Link>
        <h1 className="mt-2 text-2xl font-extrabold tracking-tight text-white">{editing ? `Edit Estimate ${documentNo}` : 'New Estimate'}</h1>
      </div>

      {lockState?.locked && (
        <div role="alert" className="flex items-start gap-2 rounded-xl border border-gold-800 bg-gold-900/20 p-3.5 text-sm text-gold-100">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-gold-300" aria-hidden="true" />
          <span>{lockState.holderName || 'Someone else'} is currently editing this estimate. Saving is disabled to avoid overwriting their changes — try again in a few minutes.</span>
        </div>
      )}

      {/* Customer */}
      <Card>
        <CardHeader title="Customer" />
        {!editing && (
          <div className="relative mb-3">
            <label htmlFor="cust-search" className={fieldLabelClass}>
              Search existing customers
            </label>
            <input
              id="cust-search"
              className={`${fieldControlClass} mt-1`}
              value={customerSearch}
              onChange={(e) => {
                setCustomerSearch(e.target.value);
                if (e.target.value.trim().length >= 2) customerSearchMutation.mutate(e.target.value);
                else setCustomerMatches([]);
              }}
            />
            {customerMatches.length > 0 && (
              <ul className="absolute z-10 mt-1 w-full overflow-hidden rounded-xl border border-ink-border bg-ink-raised shadow-card">
                {customerMatches.map((c) => (
                  <li key={c.customerId}>
                    <button
                      type="button"
                      className="block w-full px-3 py-2.5 text-left text-sm text-slate-200 hover:bg-white/5"
                      onClick={() => {
                        setForm((prev) => ({ ...prev, customerId: c.customerId, customerName: c.name, phone: c.phone || '', email: c.email || '', serviceAddress: c.address || '' }));
                        setCustomerMatches([]);
                        setCustomerSearch('');
                      }}
                    >
                      {c.name} {c.phone ? `— ${c.phone}` : ''}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          {[
            { id: 'cf-name', label: 'Name', key: 'customerName' as const, required: true },
            { id: 'cf-phone', label: 'Phone', key: 'phone' as const },
            { id: 'cf-email', label: 'Email', key: 'email' as const },
            { id: 'cf-billing', label: 'Billing Address', key: 'billingAddress' as const },
            { id: 'cf-service', label: 'Service Address', key: 'serviceAddress' as const },
          ].map((f) => (
            <div key={f.id} className="space-y-1">
              <label htmlFor={f.id} className={fieldLabelClass}>
                {f.label}
              </label>
              <input
                id={f.id}
                className={fieldControlClass}
                value={(form[f.key] as string) || ''}
                onChange={(e) => setForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
                required={f.required}
              />
            </div>
          ))}
        </div>
        {!editing && !form.customerId && (
          <Button type="button" variant="secondary" busy={duplicateCheck.isPending} busyLabel="Checking…" onClick={() => duplicateCheck.mutate()} className="mt-3">
            Check for Duplicate
          </Button>
        )}
        {duplicateCheck.data?.match && (
          <p className="mt-3 rounded-lg border border-gold-800 bg-gold-900/20 p-2.5 text-sm text-gold-100">
            Possible existing customer: {duplicateCheck.data.match.name} ({duplicateCheck.data.match.reasons.join(', ')}). Search above to reuse their record instead of creating a duplicate.
          </p>
        )}
      </Card>

      {/* Project */}
      <Card>
        <CardHeader title="Project" />
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1 sm:col-span-2">
            <label htmlFor="ef-project" className={fieldLabelClass}>
              Project / Job Name
            </label>
            <input id="ef-project" className={fieldControlClass} value={form.project} onChange={(e) => setForm((prev) => ({ ...prev, project: e.target.value }))} required />
          </div>
          <div className="space-y-1">
            <label htmlFor="ef-lead" className={fieldLabelClass}>
              Lead Source
            </label>
            <select id="ef-lead" className={fieldControlClass} value={form.leadSource} onChange={(e) => setForm((prev) => ({ ...prev, leadSource: e.target.value }))}>
              {LEAD_SOURCES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label htmlFor="ef-terms" className={fieldLabelClass}>
              Payment Terms
            </label>
            <input id="ef-terms" className={fieldControlClass} value={form.paymentTerms} onChange={(e) => setForm((prev) => ({ ...prev, paymentTerms: e.target.value }))} />
          </div>
          <div className="space-y-1 sm:col-span-2">
            <label htmlFor="ef-notes" className={fieldLabelClass}>
              Customer Notes
            </label>
            <textarea id="ef-notes" className={fieldControlClass} value={form.customerNotes} onChange={(e) => setForm((prev) => ({ ...prev, customerNotes: e.target.value }))} rows={2} />
          </div>
        </div>
      </Card>

      {/* Line items */}
      <Card>
        <CardHeader title="Line Items" />

        <div className="mb-3 flex flex-wrap items-end gap-2">
          <div className="min-w-[14rem] flex-1 space-y-1">
            <label htmlFor="pb-pick" className={fieldLabelClass}>
              Add from Price Book
            </label>
            <select id="pb-pick" className={fieldControlClass} value={priceBookPick} onChange={(e) => setPriceBookPick(e.target.value)}>
              <option value="">Select a service…</option>
              {priceBook.map((p) => (
                <option key={p.serviceId} value={p.serviceId}>
                  {p.category} — {p.name} ({p.currentPriceText}/{p.unit})
                </option>
              ))}
            </select>
          </div>
          <Button type="button" variant="secondary" disabled={!priceBookPick} onClick={addPriceBookItem}>
            Add
          </Button>
          <Button type="button" variant="secondary" onClick={addCustomItem}>
            Add Custom Item
          </Button>
        </div>

        {form.items.length === 0 && <EmptyState>No line items yet.</EmptyState>}

        {form.items.length > 0 && (
          <ul className="space-y-2">
            {form.items.map((item, i) => (
              <li key={i} className="flex flex-wrap items-end gap-2 rounded-lg border border-ink-border bg-ink-raised p-2.5">
                <div className="min-w-[10rem] flex-1 space-y-1">
                  <label htmlFor={`item-desc-${i}`} className={fieldLabelClass}>
                    Description
                  </label>
                  <input id={`item-desc-${i}`} className={fieldControlSmClass} value={item.description} onChange={(e) => updateItem(i, { description: e.target.value })} />
                </div>
                <div className="w-20 space-y-1">
                  <label htmlFor={`item-qty-${i}`} className={fieldLabelClass}>
                    Qty
                  </label>
                  <input id={`item-qty-${i}`} type="number" min="0" step="0.01" className={fieldControlSmClass} value={item.qty} onChange={(e) => updateItem(i, { qty: Number(e.target.value) })} />
                </div>
                <div className="w-24 space-y-1">
                  <label htmlFor={`item-rate-${i}`} className={fieldLabelClass}>
                    Rate
                  </label>
                  <input id={`item-rate-${i}`} type="number" min="0" step="0.01" className={fieldControlSmClass} value={item.rate} onChange={(e) => updateItem(i, { rate: Number(e.target.value) })} />
                </div>
                <p className="w-24 text-right text-sm font-semibold text-gold-200">{money(Number(item.qty || 0) * Number(item.rate || 0))}</p>
                <button type="button" onClick={() => removeItem(i)} className="text-xs font-semibold text-red-300 hover:text-red-200">
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Totals */}
      <Card raised className="border-gold-800">
        <CardHeader title="Totals" />
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <label htmlFor="ef-discount" className={fieldLabelClass}>
              Discount ($)
            </label>
            <input id="ef-discount" type="number" min="0" step="0.01" className={fieldControlClass} value={form.discount} onChange={(e) => setForm((prev) => ({ ...prev, discount: Number(e.target.value) }))} />
          </div>
          <div className="space-y-1">
            <label htmlFor="ef-tax" className={fieldLabelClass}>
              Tax Rate (%)
            </label>
            <input id="ef-tax" type="number" min="0" step="0.001" className={fieldControlClass} value={form.taxRate} onChange={(e) => setForm((prev) => ({ ...prev, taxRate: Number(e.target.value) }))} />
          </div>
        </div>
        <dl className="mt-4 space-y-1.5 border-t border-ink-border pt-3 text-sm">
          <div className="flex justify-between">
            <dt className="text-slate-400">Subtotal</dt>
            <dd className="text-slate-200">{money(totals.subtotal)}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-slate-400">Tax</dt>
            <dd className="text-slate-200">{money(totals.tax)}</dd>
          </div>
          <div className="flex justify-between text-lg font-extrabold text-gold-200">
            <dt>Total</dt>
            <dd>{money(totals.total)}</dd>
          </div>
        </dl>
      </Card>

      {saveMessage && (
        <div
          className={`space-y-1 rounded-xl border p-3.5 text-sm ${saveMessage.kind === 'success' ? 'border-success/30 bg-success-soft text-emerald-200' : 'border-danger/30 bg-danger-soft text-red-200'}`}
        >
          <p>{saveMessage.text}</p>
          {saveMessage.jobId && (
            <Link to={`/jobs/${encodeURIComponent(saveMessage.jobId)}`} className="font-semibold underline">
              View job
            </Link>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {!editing && (
          <Button variant="secondary" busy={saveDraftMutation.isPending} busyLabel="Saving…" onClick={() => saveDraftMutation.mutate()} disabled={!form.customerName.trim() || !form.project.trim()}>
            Save Draft
          </Button>
        )}
        <Button
          busy={submitMutation.isPending}
          busyLabel={editing ? 'Saving…' : 'Creating…'}
          onClick={() => submitMutation.mutate()}
          disabled={!form.customerName.trim() || !form.project.trim() || form.items.length === 0 || !!lockState?.locked}
        >
          {editing ? 'Save Changes' : 'Create Estimate'}
        </Button>
      </div>
    </div>
  );
}
